import { useParams, Navigate, Link } from "react-router-dom";
import { Smartphone, GraduationCap, Home as HomeIcon, Crown, Building2, Check, ArrowRight } from "lucide-react";
import { PageHero, CTASection } from "../components/site/Shared";
import { Reveal, Stagger, StaggerItem } from "../components/site/Reveal";
import { HealthDot } from "../components/site/Mockups";
import { ParentAppShowcase } from "../components/site/ParentApp";
import { TeacherAppShowcase } from "../components/site/TeacherApp";
import { DemoPreview, RoleDemoCTA } from "../components/site/DemoPreview";


export const ROLES = {
  teacher: {
    icon: Smartphone, label: "Teacher", tagline: "Teachers operate.",
    title: "Everything a teacher needs for the school day.",
    intro: "From the first class of the day to the last, the PARTECON Teacher App keeps execution, records and communication in one focused workspace — feeding the school's operating layer in real time.",
    caps: ["Class schedule & ongoing classes", "Attendance & lesson logging", "End-of-Class Summary (EOC)", "Assignments & evaluation", "Behaviour feedback", "Lesson plans & to-do", "Communication via PCC", "Teacher AHI"],
    helps: ["Less dependence on disconnected tools", "One place for the daily workflow", "Easier attendance & lesson recording", "Direct, governed communication"],
    visual: <div className="space-y-5"><TeacherAppShowcase limit={3} /><RoleDemoCTA role="Teacher" cta="Explore the full Teacher Experience" /></div>,
  },
  student: {
    icon: GraduationCap, label: "Student", tagline: "Students participate.",
    title: "One connected school experience.",
    intro: "Students stay connected to their academic environment — schedules, homework, exams, performance and communication in one place.",
    caps: ["Classes & schedules", "Homework & academic activities", "Examination information", "Performance & Student AHI", "Communication via PCC", "Calendar, events & notifications"],
    helps: ["Stays connected to academics", "Clear visibility of schedules & tasks", "Continuity from classroom to student", "More structured communication"],
    visual: <DemoPreview role="Student" title="Explore the Student Experience" cta="Explore the Student Experience"
      blurb="A connected experience for schedules, homework, exams, performance and communication — see the full Student App in a personalised demo." />,
  },
  parent: {
    icon: HomeIcon, label: "Parent", tagline: "Parents stay connected.",
    title: "Everything parents need from the school, connected in one place.",
    intro: "PARTECON gives parents a dedicated experience to stay connected with their child's school, academic activity, communication and important information — through the same governed operating layer that connects the entire school.",
    caps: ["Attendance visibility", "AHI — Analytical Health Index", "Academic information", "Examination progress & results", "Calendar & school events", "Notices & circulars", "PARTECON Communication Channel (PCC)", "Notifications & relevant updates"],
    helps: ["Less scattered WhatsApp-style messaging", "Structured communication with school", "Clear visibility of academics & attendance", "A documented, governed environment"],
    visual: <ParentAppShowcase />,
  },
  principal: {
    icon: Crown, label: "Principal", tagline: "Principals see.",
    title: "See the school as it operates.",
    intro: "The Principal Command Center gives leadership a central, real-time view of what is happening across the school — and what needs attention now.",
    caps: ["Classes running & ongoing classes", "Attendance & EOC compliance", "AHI & tasks requiring attention", "Teacher / student / class performance", "PCC escalations & operational alerts", "Systemic performance concerns"],
    helps: ["Real-time operational visibility", "Less dependence on delayed reports", "One view of teacher/class/student", "Faster identification of issues"],
    visual: <DemoPreview role="Principal" title="Explore the Principal Dashboard" cta="Explore the Principal Dashboard"
      blurb="School-wide real-time visibility — AHI, attendance, EOC compliance, performance, alerts and tasks that need attention. See the full Command Center in a personalised demo." />,
  },
  admin: {
    icon: Building2, label: "Administration", tagline: "Administrators manage.",
    title: "One system for school operations.",
    intro: "Administration operates from the same underlying PARTECON system — centralising school operations with role-based, permissioned access.",
    caps: ["User management & roles", "Central & school administration", "Permission-controlled access", "Activity logs & records", "Operational workflows & settings", "Centralised school data"],
    helps: ["Centralises school operations", "Reduces fragmented systems", "Controlled, role-based access", "Greater visibility across workflows"],
    visual: <DemoPreview role="Administration" title="Explore the Administration Experience" cta="Explore the Administration Experience"
      blurb="The administrative operating layer — records, workflows, communication, scheduling, reporting and school-wide coordination. See the full Administration console in a personalised demo." />,
  },
};

const ORDER = ["teacher", "student", "parent", "principal", "admin"];

export default function SolutionRole() {
  const { role } = useParams();
  const r = ROLES[role];
  if (!r) return <Navigate to="/platform" replace />;
  const others = ORDER.filter((k) => k !== role);
  return (
    <>
      <PageHero overline={`Solutions · ${r.label}`} title={r.title} sub={r.intro} />

      <section className="canvas py-20 md:py-28" data-testid={`solution-${role}`}>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <span className="overline">{r.tagline}</span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-3 text-[#1A1D26]">What the {r.label.toLowerCase()} gets</h2>
            <div className="grid sm:grid-cols-2 gap-2.5 mt-6">
              {r.caps.map((c) => (
                <div key={c} className="flex items-center gap-2.5 text-[14px] text-[#1A1D26]">
                  <span className="h-5 w-5 rounded-full bg-[#DCFCE7] flex items-center justify-center shrink-0"><Check size={12} className="text-[#16A34A]" /></span>{c}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>{r.visual}</Reveal>
        </div>
      </section>

      <section className="chrome py-16 border-y border-white/8" data-testid={`solution-${role}-helps`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <span className="overline overline-light">How PARTECON helps</span>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            {r.helps.map((h) => (
              <StaggerItem key={h}>
                <div className="glass rounded-2xl p-5 h-full">
                  <HealthDot />
                  <p className="text-white/85 text-[14px] mt-3 leading-relaxed">{h}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="canvas py-16" data-testid={`solution-${role}-others`}>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="overline">One operating layer · every role</span>
          <div className="flex flex-wrap gap-3 mt-5">
            {others.map((k) => (
              <Link key={k} to={`/solutions/${k}`} data-testid={`solution-link-${k}`}
                className="inline-flex items-center gap-2 rounded-lg bg-white border border-[#ECECE8] px-4 py-2.5 text-[13px] font-medium text-[#1A1D26] hover:border-[#6366F1] transition-colors card-hover">
                {(() => { const I = ROLES[k].icon; return <I size={15} className="text-[#6366F1]" />; })()}
                {ROLES[k].label} <ArrowRight size={14} className="text-[#9AA1AD]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

    </>
  );
}
